import React, { useRef } from 'react'
import { useNavigate, useLocation, NavigateFunction } from 'react-router-dom'
//@ts-ignore
import memoize from 'memoize'

function base64ToString(base64: string) {
  const binString = atob(base64)

  return new TextDecoder().decode(
    //@ts-ignore
    Uint8Array.from(binString, (m) => m.codePointAt(0))
  )
}

function stringToBase64(string: string) {
  const binString = Array.from(new TextEncoder().encode(string), (byte) =>
    String.fromCodePoint(byte)
  ).join('')

  return btoa(binString)
}

const decodeSearchMemo = memoize((search: string) => {
  try {
    const query = JSON.parse(base64ToString(search.replace('?', '')) || '{}')

    return query
  } catch (e) {
    console.error(e)

    return {}
  }
})
export const decodeSearch = () => decodeSearchMemo(window.location.search)

export class QuerySearch {
  navigate: NavigateFunction

  constructor(navigate: NavigateFunction) {
    this.navigate = navigate
  }
  setQuery = <query>(toBeUpdatedQuery: query, replace = false) => {
    const mergedQuery = { ...decodeSearch(), ...toBeUpdatedQuery } as query
    const mergedQueryString = JSON.stringify(mergedQuery)
    this.navigate(
      {
        pathname: window.location.pathname,
        search: stringToBase64(mergedQueryString),
      },
      { replace }
    )
  }

  setPathQuery = <query>(
    path: string,
    toBeUpdatedQuery: query,
    replace = false,
    mergeBeforeQuery = true
  ) => {
    const mergedQuery = {
      ...(mergeBeforeQuery ? decodeSearch() : {}),
      ...toBeUpdatedQuery,
    } as query
    const mergedQueryString = JSON.stringify(mergedQuery)

    return this.navigate(
      {
        pathname: path,
        search: stringToBase64(mergedQueryString),
      },
      { replace }
    )
  }

  removeQuery = <query>(toBeRemovedQuery: (keyof query)[], replace = false) => {
    const mergedQuery = { ...decodeSearch() }

    toBeRemovedQuery.forEach((prop) => {
      delete mergedQuery[prop]
    })

    const mergedQueryString = JSON.stringify(mergedQuery)

    this.navigate(
      {
        pathname: window.location.pathname,
        search: stringToBase64(mergedQueryString),
      },
      { replace }
    )
  }
}

export const useSearchQuery = <Query extends Record<any, any>>() => {
  const location = useLocation()
  const navigate = useNavigate()
  const decode = () => decodeSearchMemo(window.location.search) as Query

  const query = React.useMemo(() => {
    const queryData = decode()

    return queryData
  }, [location.search])

  const querySearch = React.useMemo(() => {
    return new QuerySearch(navigate)
  }, [])

  return {
    query,
    location,
    navigate,
    decode,
    setPathQuery: querySearch.setPathQuery<Query>,
    setQuery: querySearch.setQuery<Query>,
    removeQuery: querySearch.removeQuery<Query>,
  }
}

/*
 Использовать внутри Moodal.
 По сколько есть задержка (анимация) в закрытии модалок, а в search query его нет,
 мы теряем параменты еще до того как модалка будет закрыта.
 Это вызывает потерю нужных параметров (например при использовании хука useStorageObjects({ schemeName, prefix }))
 и ведёт к нежелательным эффектом.

 Этот хук позволяет сохранить эти параменты до момента пока модальное окно не будет размонтированно.
 сохраняет все кроме query.modal

*/

export const useSearchQueryModal = <Query extends Record<any, any>>() => {
  const searchQuery = useSearchQuery<Query>()
  const timer = useRef<NodeJS.Timeout>()
  const [prevQuery, setPrevQuery] = React.useState(searchQuery.query)

  React.useLayoutEffect(() => {
    if (prevQuery.modal && !searchQuery.query.modal) {
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = undefined
      }
      timer.current = setTimeout(() => {
        setPrevQuery(searchQuery.query)
      }, 300)
    } else {
      setPrevQuery(searchQuery.query)
    }
  }, [searchQuery.query, prevQuery.modal])

  return {
    ...searchQuery,
    query: {
      ...prevQuery,
      modal: searchQuery.query.modal,
    },
  }
}
